import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Lend } from '../../../../models/lend';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment-timezone';
import PermissionButton from '../../../../components/common/PermissionButton';

interface PaysModalWebProps {
  open: boolean;
  onClose: () => void;
  client: Lend | null;
  clientId?: number;
  canAllowDiscount: boolean;
  canAllowManualDiscount: boolean;
  canAllowAutomaticDiscount: boolean;
  canPdf: boolean;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
  onPaymentSuccess: () => void;
}

type CollectDiscountSettings = {
  days_discount?: number | string;
  enable_discount_manual?: boolean | number | string;
};

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90vw',
  maxWidth: 500,
  height: '80vh',
  maxHeight: 560,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 2,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  overflowY: 'auto',
};

const parseAmount = (value: string | number | null | undefined) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const normalizedValue = String(value ?? '').trim().replace(',', '.');
  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const normalizeDiscountDays = (value: unknown) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;
};

const getLendAgeDays = (lendDate: unknown) => {
  if (!lendDate) return null;

  const parsedDate = new Date(String(lendDate));
  if (Number.isNaN(parsedDate.getTime())) return null;

  const today = new Date();
  const utcStart = Date.UTC(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
  );
  const utcToday = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  return Math.max(0, Math.floor((utcToday - utcStart) / 86400000));
};

const PaysModalWeb: React.FC<PaysModalWebProps> = ({
  open,
  onClose,
  client,
  clientId,
  canAllowDiscount,
  canAllowManualDiscount,
  canAllowAutomaticDiscount,
  canPdf,
  showAlert,
  showLoading,
  hideLoading,
  onPaymentSuccess,
}) => {
  const [valorAbono, setValorAbono] = useState('0');
  const [valorDescuento, setValorDescuento] = useState('0');
  const [selectedFormPay, setSelectedFormPay] = useState('1');
  const [selectedBank, setSelectedBank] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [saldoRestante, setSaldoRestante] = useState(client?.balance || 0);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [location, setLocation] = useState<string | null>(null);
  const [banks, setBanks] = useState(Array<{ id: number; name: string }>());
  const [discountEnabled, setDiscountEnabled] = useState(false);

  const collectSettings = useMemo(
    () => ((client?.collect ?? null) as CollectDiscountSettings | null),
    [client],
  );
  const baseBalance = Number(client?.balance || 0);
  const discountDays = normalizeDiscountDays(collectSettings?.days_discount);
  const canUseManualDiscount = canAllowManualDiscount;
  const canUseAutomaticDiscount = canAllowAutomaticDiscount;
  const canUseAnyDiscount =
    canAllowDiscount || canUseManualDiscount || canUseAutomaticDiscount;
  const lendAgeDays = getLendAgeDays(client?.date);
  const utilityValue = Math.max(0, Number((client as any)?.value_utility ?? 0));
  const canApplyCalculatedDiscount =
    canUseAutomaticDiscount &&
    lendAgeDays !== null &&
    lendAgeDays <= discountDays &&
    utilityValue > 0;
  const calculatedDiscountAmount = discountEnabled
    ? Math.max(0, utilityValue * 0.5)
    : 0;

  const resetForm = () => {
    setValorAbono('0');
    setValorDescuento('0');
    setSelectedFormPay('1');
    setSelectedBank('');
    setReceiptNumber('');
    setSaldoRestante(client?.balance || 0);
    setErrorMessage('');
    setIsPaymentComplete(false);
    setDiscountEnabled(false);
  };

  const fetchCoordinates = async () => {
    try {
      const coords = await getCoordinates();
      setLocation(coords);
    } catch (error) {
      showAlert('No se puede registrar sin activar el GPS.', 'error');
    }
  };

  const fetchBanks = async () => {
    try {
      const { data } = await axios.get('/banks');
      setBanks(data.data);
    } catch (error) {
      showAlert('Error al cargar los bancos.', 'error');
    }
  };

  useEffect(() => {
    fetchBanks();
    fetchCoordinates();
  }, []);

  useEffect(() => {
    if (open && client) {
      setValorAbono('0');
      setValorDescuento('0');
      setSelectedFormPay('1');
      setSelectedBank('');
      setReceiptNumber('');
      setSaldoRestante(client?.balance || 0);
      setErrorMessage('');
      setIsPaymentComplete(false);
      setDiscountEnabled(false);
    }
  }, [open, client, canAllowDiscount, canAllowManualDiscount, canAllowAutomaticDiscount]);

  useEffect(() => {
    if (!canApplyCalculatedDiscount && discountEnabled) {
      setDiscountEnabled(false);
      setValorDescuento('0');
    }
  }, [canApplyCalculatedDiscount, discountEnabled]);

  useEffect(() => {
    if (canApplyCalculatedDiscount && discountEnabled) {
      setValorDescuento(String(Math.max(0, utilityValue * 0.5)));
    }
  }, [canApplyCalculatedDiscount, discountEnabled, utilityValue]);

  useEffect(() => {
    if (canApplyCalculatedDiscount && discountEnabled) {
      setValorAbono(String(Math.max(0, baseBalance - Math.max(0, utilityValue * 0.5))));
    }
  }, [baseBalance, canApplyCalculatedDiscount, discountEnabled, utilityValue]);

  useEffect(() => {
    if (!client) return;

    const abonoValue = parseAmount(valorAbono);
    let discountValue = 0;

    if (canUseAnyDiscount) {
      if (canUseManualDiscount) {
        const rawDiscount = Math.min(
          Math.max(0, parseAmount(valorDescuento)),
          baseBalance,
        );
        discountValue = Math.min(rawDiscount, Math.max(0, baseBalance - abonoValue));
      } else {
        discountValue = canApplyCalculatedDiscount && discountEnabled
          ? Math.max(0, utilityValue * 0.5)
          : 0;
      }
    }

    const maxForAbono = Math.max(0, baseBalance - discountValue);
    if (abonoValue > maxForAbono) {
      setValorAbono(String(maxForAbono));
      setSaldoRestante(Math.max(0, baseBalance - maxForAbono - discountValue));
      return;
    }

    setSaldoRestante(Math.max(0, baseBalance - abonoValue - discountValue));
  }, [
    baseBalance,
    canApplyCalculatedDiscount,
    canUseAnyDiscount,
    canUseManualDiscount,
    client,
    discountEnabled,
    utilityValue,
    valorAbono,
    valorDescuento,
  ]);

  const handleAbonoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    const abono = parseAmount(nextValue);
    const rawManualDiscount = Math.min(
      Math.max(0, parseAmount(valorDescuento)),
      baseBalance,
    );
    const discountValue = canUseAnyDiscount
      ? canUseManualDiscount
        ? rawManualDiscount
        : canApplyCalculatedDiscount && discountEnabled
          ? Math.max(0, utilityValue * 0.5)
          : 0
      : 0;
    const maxValue = Math.max(0, baseBalance - discountValue);
    const nuevoSaldo = baseBalance - (abono + discountValue);

    if (abono > maxValue) {
      setErrorMessage(
        canUseManualDiscount
          ? 'La suma del abono y el descuento no puede superar el saldo.'
          : 'El valor del abono no puede exceder el saldo disponible.',
      );
      setValorAbono(String(maxValue));
      setSaldoRestante(Math.max(0, baseBalance - maxValue - discountValue));
    } else {
      setErrorMessage('');
      setValorAbono(nextValue);
      setSaldoRestante(Math.max(0, nuevoSaldo));
    }
  };

  const handleDescuentoManualChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canUseManualDiscount) {
      return;
    }

    const nextValue = event.target.value;
    let descuento = Math.min(
      Math.max(0, parseAmount(nextValue)),
      baseBalance,
    );
    const abono = parseAmount(valorAbono);

    if (abono + descuento > baseBalance) {
      descuento = Math.max(0, baseBalance - abono);
      setErrorMessage('La suma del abono y el descuento no puede superar el saldo.');
    } else {
      setErrorMessage('');
    }

    setValorDescuento(String(descuento));
    setSaldoRestante(Math.max(0, baseBalance - abono - descuento));
  };

  const handleAccept = async () => {
    setIsSaving(true);

    if (!location) {
      showAlert('No está activo el GPS para realizar el registro.', 'warning');
      setIsSaving(false);
      return;
    }

    if (selectedFormPay === '0' && (!selectedBank || !receiptNumber)) {
      setErrorMessage('Debe seleccionar un banco y proporcionar el número de recibo.');
      setIsSaving(false);
      return;
    }

    const currentDateTimeBogota = moment().tz('America/Bogota').format('YYYY-MM-DD HH:mm:ss');
    const paymentAbono = parseAmount(valorAbono);
    const appliedDiscountTotal = canUseAnyDiscount
      ? canUseManualDiscount
        ? Math.min(
            Math.max(0, parseAmount(valorDescuento)),
            baseBalance,
            Math.max(0, baseBalance - paymentAbono),
          )
        : canApplyCalculatedDiscount && discountEnabled
          ? Math.max(0, utilityValue * 0.5)
          : 0
      : 0;

    if (paymentAbono + appliedDiscountTotal > baseBalance) {
      setErrorMessage('La suma del abono y el descuento no puede superar el saldo.');
      setIsSaving(false);
      return;
    }

    const formData = {
      lend_id: Number(clientId),
      location,
      user_id: 1,
      bank_id: selectedFormPay === '0' ? selectedBank : null,
      receipt_bank: selectedFormPay === '0' ? receiptNumber : null,
      balance: Math.max(0, baseBalance - paymentAbono - appliedDiscountTotal),
      cash: selectedFormPay === '1',
      value: valorAbono,
      value_discount: appliedDiscountTotal,
      state: true,
      pay_date: currentDateTimeBogota,
      date_operation: currentDateTimeBogota,
      invoice: `INV-${clientId}-${Date.now()}`,
      route_id: 1,
    };

    try {
      showLoading();
      await axios.post('pays', formData);
      showAlert('Se ha realizado el abono exitosamente!', 'success');
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsPaymentComplete(true);
      onPaymentSuccess();
      onClose();
    } catch (error) {
      showAlert('Error fetching data!', 'error');
    } finally {
      setIsSaving(false);
      hideLoading();
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getCoordinates = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const coordinates = `${lat}, ${lng}`;
            resolve(coordinates);
          },
          error => {
            reject(error);
          }
        );
      } else {
        showAlert('Geolocation is not supported by this browser.', 'warning');
        reject(new Error('Geolocation is not supported by this browser.'));
      }
    });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            bgcolor: 'primary.main',
            color: 'white',
          }}
        >
          <Typography variant="h6">Abono</Typography>
          <IconButton
            onClick={handleClose}
            sx={{ color: 'white' }}
            aria-label="cerrar"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 3, overflowY: 'auto', flexGrow: 1 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <Typography variant="body1">
            Agregar abono al préstamo #{clientId} para el cliente: {client?.client.fullname} (Nit: {client?.client.idnumber})
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body1" gutterBottom>
            Saldo: {client?.balance}
          </Typography>
          {(parseAmount(valorAbono) > 0 || parseAmount(valorDescuento) > 0) && (
            <Typography variant="body1" gutterBottom>
              Nuevo Saldo: {saldoRestante}
            </Typography>
          )}
          <Typography variant="body1" gutterBottom>
            Días Atraso: {client?.days_late}
          </Typography>
          {canUseAutomaticDiscount && (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Días desde préstamo: {lendAgeDays ?? 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Utilidad base descuento: {utilityValue}
              </Typography>
            </>
          )}
          <Divider sx={{ mb: 2 }} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Forma de Pago</InputLabel>
            <Select
              value={selectedFormPay}
              onChange={(e) => setSelectedFormPay(e.target.value as string)}
              label="Forma de Pago"
              disabled={isPaymentComplete}
            >
              <MenuItem value="1">Efectivo</MenuItem>
              <MenuItem value="0">Transferencia</MenuItem>
            </Select>
          </FormControl>
          {selectedFormPay === '0' && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Banco</InputLabel>
                <Select
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value as string)}
                  label="Banco"
                  disabled={isPaymentComplete}
                >
                  {banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Nro Recibo"
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
                sx={{ mb: 2 }}
                disabled={isPaymentComplete}
              />
            </>
          )}
          <TextField
            fullWidth
            label="Valor Abono"
            value={valorAbono}
            onChange={handleAbonoChange}
            onFocus={() => {
              if (valorAbono === '0') setValorAbono('');
            }}
            onBlur={() => {
              if (valorAbono.trim() === '') setValorAbono('0');
            }}
            sx={{ mb: 2 }}
            disabled={isPaymentComplete || (canUseAutomaticDiscount && discountEnabled)}
          />
          {canUseManualDiscount && baseBalance > 0 && (
            <TextField
              fullWidth
              label="Valor Descuento"
              value={valorDescuento}
              onChange={handleDescuentoManualChange}
              onFocus={() => {
                if (valorDescuento === '0') setValorDescuento('');
              }}
              onBlur={() => {
                if (valorDescuento.trim() === '') setValorDescuento('0');
              }}
              sx={{ mb: 2 }}
              disabled={isPaymentComplete}
              helperText="Descuento manual: abono + descuento no puede superar el saldo."
            />
          )}
          {canApplyCalculatedDiscount && (
            <>
              <FormControlLabel
                sx={{ mb: 1 }}
                control={(
                  <Checkbox
                    checked={discountEnabled}
                    onChange={(event) => {
                      const nextValue = event.target.checked;
                      setDiscountEnabled(nextValue);
                      if (!nextValue) {
                        setValorDescuento('0');
                      }
                    }}
                    disabled={isPaymentComplete}
                  />
                )}
                label="Descuento"
              />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Descuento calculado (50% utilidad): {Math.max(0, utilityValue * 0.5)}
              </Typography>
              {discountEnabled && (
                <TextField
                  fullWidth
                  label="Valor Descuento"
                  value={valorDescuento}
                  sx={{ mb: 2 }}
                  disabled
                />
              )}
            </>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          {!isPaymentComplete && (
            <>
              <Button variant="outlined" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleAccept}
                disabled={isSaving}
              >
                Guardar
              </Button>
            </>
          )}
          {isPaymentComplete && (
            <>
              <Button variant="outlined" onClick={handleClose}>
                Cerrar
              </Button>
              <PermissionButton
                allowed={canPdf}
                deniedTooltip="Sin permiso para PDF"
                tooltip="Enviar PDF"
                variant="contained"
                onClick={() => alert('PDF generado')}
              >
                Enviar PDF
              </PermissionButton>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default PaysModalWeb;
